
import { PreviewUser, PreviewBusiness } from '@/hooks/usePreviewData';

export type TemplateType = 'email_confirmation' | 'password_reset' | 'welcome' | 'business_registration' | 'business_approval' | 'business_listed' | 'pending_subscription_business_listed' | 'listed_for_free' | 'review_notification' | 'offline_notification' | 'job_proposal_notification' | 'proposal_response_notification' | 'admin_business_pending_approval' | 'subscription_pending_completion' | 'incomplete_business_profile' | 'inactive_community_members';

export interface EmailPreviewData {
  user?: PreviewUser;
  business?: PreviewBusiness;
  reviewer?: PreviewUser;
  rating?: number;
  communityName: string;
  communityColor: string;
  communityUrl: string;
  emailDomain: string;
  supportEmail: string;
}

// Interface for campaign preview data (for CampaignManagement component)
export interface CampaignPreviewData {
  user_id: string;
  business_id?: string;
  user_email: string;
  user_name: string;
  business_name?: string;
  created_at: string;
}

export function buildTemplateVariables(
  templateType: TemplateType,
  data: EmailPreviewData
): Record<string, string> {
  const baseVariables = {
    community_name: data.communityName,
    community_color: data.communityColor,
    community_url: data.communityUrl,
    support_email: data.supportEmail,
    current_year: new Date().getFullYear().toString(),
  };

  switch (templateType) {
    case 'email_confirmation':
    case 'password_reset':
      return {
        ...baseVariables,
        user_name: data.user ? `${data.user.firstname} ${data.user.lastname}`.trim() : 'John Doe',
        user_email: data.user?.email || 'user@example.com',
        confirmation_url: `${data.communityUrl}/verify?token=sample_token&type=${templateType}`,
        reset_url: `${data.communityUrl}/verify?token=sample_token&type=${templateType}`,
      };

    case 'welcome':
      return {
        ...baseVariables,
        user_name: data.user ? `${data.user.firstname} ${data.user.lastname}`.trim() : 'John Doe',
        user_email: data.user?.email || 'user@example.com',
      };

    case 'business_registration':
      return {
        ...baseVariables,
        user_name: data.business ? `${data.business.user_profiles.firstname} ${data.business.user_profiles.lastname}`.trim() : 'Business Owner',
        user_email: data.business?.user_profiles.email || 'owner@example.com',
        business_name: data.business?.name || 'Sample Business',
        business_id: data.business?.id || 'sample-business-id',
        dashboard_url: `${data.communityUrl}/admin/my-business`,
        review_timeline: '2-3 business days',
      };

    case 'business_approval':
      // For preview purposes, show subscription note for Paid businesses (default mock business type is Paid)
      const businessDiscountType = 'Paid'; // Mock data assumes Paid business for preview
      const subscriptionNoteHtml = businessDiscountType === 'Free' ? '' : `
        <div style="background-color: #fff3cd; border: 1px solid #ffeaa7; border-radius: 5px; padding: 15px; margin: 20px 0;">
          <h4 style="color: #856404; margin: 0 0 10px 0; font-size: 16px;">📋 Subscription Required</h4>
          <p style="color: #856404; margin: 0; font-size: 14px;">
            Please note: Your business will need an active subscription to receive messages and job opportunities from customers. 
            You can choose your subscription plan after confirming your listing.
          </p>
        </div>
      `;
      
      return {
        ...baseVariables,
        user_name: data.business ? `${data.business.user_profiles.firstname} ${data.business.user_profiles.lastname}`.trim() : 'Business Owner',
        user_email: data.business?.user_profiles.email || 'owner@example.com',
        business_name: data.business?.name || 'Sample Business',
        business_id: data.business?.id || 'sample-business-id',
        subscription_url: `${data.communityUrl}/admin/business/subscription`,
        dashboard_url: `${data.communityUrl}/admin/my-business`,
        listing_confirmation_url: `${data.communityUrl}/admin/my-business?confirm_listing=true`,
        subscription_note_html: subscriptionNoteHtml, // Show note in preview for Paid businesses
      };

    case 'business_listed':
      return {
        ...baseVariables,
        user_name: data.business ? `${data.business.user_profiles.firstname} ${data.business.user_profiles.lastname}`.trim() : 'Business Owner',
        user_email: data.business?.user_profiles.email || 'owner@example.com',
        business_name: data.business?.name || 'Sample Business',
        business_id: data.business?.id || 'sample-business-id',
        dashboard_url: `${data.communityUrl}/admin/my-business`,
      };

    case 'pending_subscription_business_listed':
      return {
        ...baseVariables,
        user_name: data.business ? `${data.business.user_profiles.firstname} ${data.business.user_profiles.lastname}`.trim() : 'Business Owner',
        user_email: data.business?.user_profiles.email || 'owner@example.com',
        business_name: data.business?.name || 'Sample Business',
        business_id: data.business?.id || 'sample-business-id',
        subscription_url: `${data.communityUrl}/admin/business/subscription`,
        dashboard_url: `${data.communityUrl}/admin/my-business`,
      };

    case 'listed_for_free':
      return {
        ...baseVariables,
        user_name: data.business ? `${data.business.user_profiles.firstname} ${data.business.user_profiles.lastname}`.trim() : 'Business Owner',
        user_email: data.business?.user_profiles.email || 'owner@example.com',
        business_name: data.business?.name || 'Sample Business',
        business_id: data.business?.id || 'sample-business-id',
        listing_confirmation_url: `${data.communityUrl}/admin/my-business?confirm_listing=true`,
        dashboard_url: `${data.communityUrl}/admin/my-business`,
      };

    case 'review_notification':
      return {
        ...baseVariables,
        business_owner_name: data.business ? `${data.business.user_profiles.firstname} ${data.business.user_profiles.lastname}`.trim() : 'Business Owner',
        user_email: data.business?.user_profiles.email || 'owner@example.com',
        business_name: data.business?.name || 'Sample Business',
        business_id: data.business?.id || 'sample-business-id',
        business_page_url: `${data.communityUrl}/service/${data.business?.slug || data.business?.id || 'sample-business-slug'}`,
        reviewer_name: data.reviewer ? `${data.reviewer.firstname} ${data.reviewer.lastname}`.trim() : 'A Customer',
        review_rating: `${data.rating || 5} star${(data.rating || 5) !== 1 ? 's' : ''}`,
        review_date: new Date().toLocaleDateString(),
      };

    case 'offline_notification':
      // Format messages using the compact logic for preview
      const mockMessages = [
        {
          content: 'Hello, are you available?',
          sender_id: 'sender123',
          sent_at: '2024-12-21T14:30:00Z'
        },
        {
          content: 'Yes, I\'m here. How can I help?',
          sender_id: 'receiver456',  
          sent_at: '2024-12-21T14:45:00Z'
        },
        {
          content: 'I have a question about your services.',
          sender_id: 'sender123',
          sent_at: '2024-12-21T15:00:00Z'
        }
      ];
      
      const formattedMessages = formatMessages(mockMessages, 'sender123');
      const conversationHistoryHtml = formatMessagesForHTML(formattedMessages, data.communityColor);
      
      return {
        ...baseVariables,
        receiver_name: data.user ? `${data.user.firstname} ${data.user.lastname}`.trim() : 'John Doe',
        sender_name: data.reviewer ? `${data.reviewer.firstname} ${data.reviewer.lastname}`.trim() : 'Someone',
        conversation_history: conversationHistoryHtml,
        conversation_url: `${data.communityUrl}/conversations/sample-conversation-id`,
      };

    case 'job_proposal_notification':
      const fullServiceRequest = 'I need help with plumbing repairs in my kitchen. The sink is leaking and needs urgent attention. This is happening since yesterday and water is getting everywhere. I have already turned off the main water supply but I really need someone to come and fix this as soon as possible. The problem seems to be with the main faucet connection and possibly some pipes underneath the sink.';
      const shortServiceRequest = 'I need help with plumbing repairs in my kitchen. The sink...';
      
      return {
        ...baseVariables,
        business_owner_name: data.business ? `${data.business.user_profiles.firstname} ${data.business.user_profiles.lastname}`.trim() : 'Business Owner',
        business_name: data.business?.name || 'Sample Business',
        business_id: data.business?.id || 'sample-business-id',
        service_request_summary: shortServiceRequest, // Sanitized short version for subject
        service_request_full: fullServiceRequest, // Complete original text for email body
        service_location: 'Downtown Area, 12345',
        service_urgency: 'Urgently (within 24 hours)',
        user_comment: 'Please contact me as soon as possible. I am available in the afternoons.',
        review_url: `${data.communityUrl}/admin/job-proposals/sample-proposal-id`,
        job_proposal_id: 'sample-proposal-id',
        service_request_id: 'sample-request-id',
      };

    case 'proposal_response_notification':
      const businessName = data.business?.name || 'Sample Business';
      const proposalStatus = 'PROPOSED'; // Default status for preview
      
      // Pre-process conditional logic for status display (matching send-email function)
      let responseStatusText = 'Response Received';
      let responseStatusColor = '#374151';
      let responseStatusBgColor = '#f3f4f6';
      let responseDescription = `${businessName} has responded to your service request. View the full details of their response.`;

      switch (proposalStatus) {
        case 'PROPOSED':
          responseStatusText = 'Proposal Submitted';
          responseStatusColor = '#166534';
          responseStatusBgColor = '#dcfce7';
          responseDescription = `${businessName} has submitted a proposal for your service request. View the full details including pricing and their response message.`;
          break;
        case 'DECLINED':
          responseStatusText = 'Request Declined';
          responseStatusColor = '#dc2626';
          responseStatusBgColor = '#fecaca';
          responseDescription = `${businessName} has declined your service request. View their response to understand why and explore other options.`;
          break;
        case 'REQUESTED_INFO':
          responseStatusText = 'More Information Requested';
          responseStatusColor = '#d97706';
          responseStatusBgColor = '#fef3c7';
          responseDescription = `${businessName} needs more information before they can provide a proposal. View their message and respond with the additional details.`;
          break;
      }

      return {
        ...baseVariables,
        business_name: businessName,
        service_request_summary: 'I need help with plumbing repairs in my kitchen. The sink is leaking and needs urgent attention.',
        proposal_status: proposalStatus,
        response_status_text: responseStatusText,
        response_status_color: responseStatusColor,
        response_status_bg_color: responseStatusBgColor,
        response_description: responseDescription,
        service_request_id: 'sample-request-id',
        user_name: data.user ? `${data.user.firstname} ${data.user.lastname}`.trim() : 'John Doe',
      };

    case 'admin_business_pending_approval':
      // Format link variables for preview
      const formatPreviewLink = (url: string, text: string) => {
        return url ? `<a href="${url}" style="color: ${data.communityColor}; text-decoration: none;">${text}</a>` : 'Not provided';
      };
      
      const website = 'https://samplebusiness.com';
      const facebook = 'https://facebook.com/samplebusiness';
      const instagram = 'https://instagram.com/samplebusiness';
      const tiktok = 'https://tiktok.com/@samplebusiness';
      const googleMaps = 'https://maps.google.com/samplebusiness';
      
      return {
        ...baseVariables,
        admin_name: 'Admin',
        business_name: data.business?.name || 'Sample Business',
        business_id: data.business?.id || 'sample-business-id',
        business_description: 'We provide professional plumbing services with over 10 years of experience. Our team is dedicated to quality workmanship and customer satisfaction.',
        business_category: 'Home Services',
        business_owner_name: data.business ? `${data.business.user_profiles.firstname} ${data.business.user_profiles.lastname}`.trim() : 'John Smith',
        business_owner_email: data.business?.user_profiles.email || 'john.smith@example.com',
        contact_email: data.business?.user_profiles.email || 'john.smith@example.com',
        contact_phone: '+44 7700 900123',
        full_address: '123 High Street, City Centre, London, SW1A 1AA, United Kingdom',
        website: website,
        facebook_url: facebook,
        instagram_url: instagram,
        tiktok_url: tiktok,
        google_maps_url: googleMaps,
        // Formatted link variables
        website_link: formatPreviewLink(website, website),
        facebook_link: formatPreviewLink(facebook, 'View Profile'),
        instagram_link: formatPreviewLink(instagram, 'View Profile'),
        tiktok_link: formatPreviewLink(tiktok, 'View Profile'),
        google_maps_link: formatPreviewLink(googleMaps, 'View Location'),
        discount_type: 'Paid',
        discount_amount_formatted: '15% off',
        discount_description: 'New customer discount - 15% off first service call',
        application_comments: 'We are excited to join your community and provide quality services to local residents. We have all necessary insurance and certifications.',
        registration_date: new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' }),
        review_url: `${data.communityUrl}/admin/dashboard`,
      };

    case 'subscription_pending_completion':
      return {
        ...baseVariables,
        user_name: data.business ? `${data.business.user_profiles.firstname} ${data.business.user_profiles.lastname}`.trim() : 'Business Owner',
        user_email: data.business?.user_profiles.email || 'owner@example.com',
        business_name: data.business?.name || 'Sample Business',
        business_id: data.business?.id || 'sample-business-id',
        subscription_url: `${data.communityUrl}/admin/business/subscription`,
        dashboard_url: `${data.communityUrl}/admin/my-business`,
      };

    case 'incomplete_business_profile':
      return {
        ...baseVariables,
        user_name: data.business ? `${data.business.user_profiles.firstname} ${data.business.user_profiles.lastname}`.trim() : 'Business Owner',
        user_email: data.business?.user_profiles.email || 'owner@example.com',
        business_name: data.business?.name || 'Sample Business',
        business_id: data.business?.id || 'sample-business-id',
        business_profile_url: `${data.communityUrl}/admin/my-business`,
      };

    case 'inactive_community_members':
      return {
        ...baseVariables,
        user_name: data.user ? `${data.user.firstname} ${data.user.lastname}`.trim() : 'John Doe',
        user_email: data.user?.email || 'user@example.com',
        services_url: `${data.communityUrl}/services`,
      };

    default:
      return baseVariables;
  }
}

// Compact helper function to format messages (matching the Edge Function logic)
function formatMessages(messages: any[], senderId: string) {
  const sortedMessages = [...messages].sort((a, b) => {
    return new Date(a.sent_at).getTime() - new Date(b.sent_at).getTime();
  });
  
  return sortedMessages.map((message, index) => {
    const sentDate = new Date(message.sent_at);
    const formattedTime = sentDate.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    const formattedDate = sentDate.toLocaleDateString();
    
    return {
      content: message.content,
      isFromSender: message.sender_id === senderId,
      time: `${formattedDate} ${formattedTime}`,
      isLatest: index === sortedMessages.length - 1
    };
  });
}

// Compact HTML formatting function with improved font size for better readability
function formatMessagesForHTML(formattedMessages: any[], communityColor: string): string {
  if (!formattedMessages || formattedMessages.length === 0) {
    return '<div style="text-align: center; color: #6b7280; font-style: italic; padding: 12px;">No previous messages</div>';
  }

  const messagesHtml = formattedMessages.map(msg => {
    // Compact message styles with improved font size for readability
    const baseMessageStyle = `
      margin-bottom: 8px;
      padding: 8px 12px;
      border-radius: 8px;
      max-width: 70%;
      word-wrap: break-word;
      font-size: 15px;
      line-height: 1.4;
    `;
    
    // Sender message styles (left-aligned, gray)
    const senderMessageStyle = `
      ${baseMessageStyle}
      background-color: #f3f4f6;
      color: #111827;
      margin-right: 30%;
    `;
    
    // Receiver message styles (right-aligned, purple tint)
    const receiverMessageStyle = `
      ${baseMessageStyle}
      background-color: rgba(124, 58, 237, 0.1);
      color: #111827;
      margin-left: 30%;
    `;
    
    // Latest message gets special styling
    const latestMessageStyle = msg.isLatest ? `
      border-left: 3px solid ${communityColor};
      background-color: #f8f5ff !important;
      padding-left: 12px;
    ` : '';
    
    const messageStyle = msg.isFromSender ? senderMessageStyle : receiverMessageStyle;
    const finalStyle = messageStyle + latestMessageStyle;
    
    // Compact time style
    const timeStyle = `
      font-size: 11px;
      color: #6b7280;
      margin-top: 3px;
      ${msg.isFromSender ? 'text-align: left; margin-right: 30%;' : 'text-align: right; margin-left: 30%;'}
    `;
    
    return `
      <div style="${finalStyle}">
        <div>${msg.content}</div>
        <div style="${timeStyle}">
          ${msg.time}
          ${msg.isLatest ? '<span style="background-color: #f97316; color: white; padding: 1px 4px; border-radius: 6px; font-size: 9px; font-weight: 500; margin-left: 6px;">New</span>' : ''}
        </div>
      </div>
    `;
  }).join('');

  return `
    <div style="
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 12px;
      background-color: #fafafa;
      margin: 16px 0;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    ">
      <div style="font-weight: 600; color: #111827; margin-bottom: 8px; font-size: 13px;">Recent Conversation</div>
      ${messagesHtml}
    </div>
  `;
}

export function processTemplate(template: string, variables: Record<string, string>): string {
  let processed = template;
  for (const [key, value] of Object.entries(variables)) {
    const regex = new RegExp(`{{${key}}}`, 'g');
    processed = processed.replace(regex, value || '');
  }
  return processed;
}

export function getDisplayAsOptions(templateType: TemplateType): Array<'user' | 'business' | 'reviewer'> {
  switch (templateType) {
    case 'email_confirmation':
    case 'password_reset':
    case 'welcome':
    case 'inactive_community_members':
      return ['user'];
    case 'business_registration':
    case 'business_approval':
    case 'business_listed':
    case 'pending_subscription_business_listed':
    case 'listed_for_free':
    case 'job_proposal_notification':
    case 'admin_business_pending_approval':
    case 'subscription_pending_completion':
    case 'incomplete_business_profile':
      return ['business'];
    case 'proposal_response_notification':
      return ['user'];
    case 'review_notification':
      return ['business', 'reviewer'];
    case 'offline_notification':
      return ['user', 'reviewer'];
    default:
      return [];
  }
}

// Helper function to convert campaign data to EmailPreviewData format
export function campaignDataToPreviewData(
  campaignData: CampaignPreviewData,
  communityName: string,
  communityColor: string,
  communityUrl: string,
  emailDomain: string,
  supportEmail: string
): EmailPreviewData {
  // Create user object from campaign data
  const user: PreviewUser = {
    id: campaignData.user_id,
    firstname: campaignData.user_name.split(' ')[0] || '',
    lastname: campaignData.user_name.split(' ').slice(1).join(' ') || '',
    email: campaignData.user_email
  };

  // Create business object if business data exists
  const business: PreviewBusiness | undefined = campaignData.business_id && campaignData.business_name ? {
    id: campaignData.business_id,
    name: campaignData.business_name,
    user_id: campaignData.user_id,
    contact_email: campaignData.user_email,
    user_profiles: {
      firstname: user.firstname,
      lastname: user.lastname,
      email: user.email
    }
  } : undefined;

  return {
    user,
    business,
    communityName,
    communityColor,
    communityUrl,
    emailDomain,
    supportEmail
  };
}
